import React, {FC} from "react";
import {EyeOutlined} from "@ant-design/icons";
import {Button, Space, Tooltip} from "antd";
import {useTranslation} from "react-i18next";
import {FieldTile, StatusTag} from "../../../Components/ui";
import {operationalCertificateStatusTone} from "../participantOperationsHelpers";
import {DetailPanel, EnumTag, FIELD_GRID_STYLE} from "./participantDetailUi";
import {QrInvitationStateTag} from "./QrInvitationSection";
import type {ParticipantDetailModel} from "./useParticipantDetail";

// Read-only summary of the primary registration, its QR code and the latest certificate.
export const SystemDataSection: FC<{model: ParticipantDetailModel}> = ({model}) => {
    const {t} = useTranslation();
    const {primaryRegistration, latestCertificate, qr} = model;
    const viewQrLabel = t("qr-view", {defaultValue: "View QR code"});

    return (
        <DetailPanel title={t("system-data-title", {defaultValue: "System data"})}>
            <div style={FIELD_GRID_STYLE}>
                <FieldTile label={t("registration-status", {defaultValue: "Registration status"})}>
                    {primaryRegistration
                        ? <EnumTag value={primaryRegistration.status}/>
                        : <StatusTag>{t("no-registration", {defaultValue: "None"})}</StatusTag>}
                </FieldTile>
                <FieldTile
                    label={t("qr-invitation", {defaultValue: "QR invitation"})}
                    action={(
                        <Tooltip title={viewQrLabel}>
                            <Button
                                size="small"
                                icon={<EyeOutlined/>}
                                aria-label={viewQrLabel}
                                disabled={!qr.primaryCodeId}
                                onClick={() => qr.openModal(qr.primaryCodeId)}
                            />
                        </Tooltip>
                    )}
                >
                    <QrInvitationStateTag state={qr.primaryState}/>
                </FieldTile>
                <FieldTile label={t("certificate", {defaultValue: "Certificate"})}>
                    {latestCertificate ? (
                        <Space direction="vertical" size={4}>
                            {latestCertificate.status
                                ? <EnumTag value={latestCertificate.status} tone={operationalCertificateStatusTone(latestCertificate.status)}/>
                                : null}
                            <StatusTag tone={latestCertificate.fileId ? "info" : "neutral"}>
                                {latestCertificate.fileId
                                    ? t("document-ready", {defaultValue: "Available"})
                                    : t("document-unavailable", {defaultValue: "Unavailable"})}
                            </StatusTag>
                        </Space>
                    ) : (
                        <StatusTag>{t("certificate-not-ready", {defaultValue: "Unready"})}</StatusTag>
                    )}
                </FieldTile>
            </div>
        </DetailPanel>
    );
};
